import React, { useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText,
  ThemeProvider, Toolbar, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PeopleIcon from '@mui/icons-material/People';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DevicesIcon from '@mui/icons-material/Devices';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

import { createAppTheme } from './theme';
import { ThemeAnbieter, useThemeMode } from './kontext/ThemeKontext';
import { Logo } from './komponenten/Bausteine';
import Login from './seiten/Login';
import Aktivierung from './seiten/Aktivierung';
import Verlaengerung from './seiten/Verlaengerung';
import Uebersicht from './seiten/Uebersicht';
import Kunden from './seiten/Kunden';
import Kundenakte from './seiten/Kundenakte';
import Installationen from './seiten/Installationen';
import Releases from './seiten/Releases';
import Protokoll from './seiten/Protokoll';
import Einstellungen from './seiten/Einstellungen';

const DRAWER_BREITE = 232;
const TOPBAR = 64;

const PUNKTE = [
  { label: 'Übersicht', pfad: '/uebersicht', icon: <ChecklistIcon fontSize="small" /> },
  { label: 'Kunden', pfad: '/kunden', icon: <PeopleIcon fontSize="small" /> },
  { label: 'Installationen', pfad: '/installationen', icon: <DevicesIcon fontSize="small" /> },
  { label: 'Releases', pfad: '/releases', icon: <CloudUploadIcon fontSize="small" /> },
  { label: 'Protokoll', pfad: '/protokoll', icon: <HistoryIcon fontSize="small" /> },
  { label: 'Einstellungen', pfad: '/einstellungen', icon: <SettingsIcon fontSize="small" /> },
];

const hatToken = () => !!localStorage.getItem('portal_token');

// Der Anmeldezustand wird beim RENDERN der Weiche gelesen, nicht im element der
// Route - das wuerde nur einmal beim Start ausgewertet (im ERP die Ursache des
// schwarzen Bildschirms beim Abmelden).
function Geschuetzt({ children }) {
  if (!hatToken()) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function StartWeiche() {
  return <Navigate to={hatToken() ? '/uebersicht' : '/login'} replace />;
}

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useThemeMode();
  const breit = useMediaQuery('(min-width:900px)');
  const [menuOffen, setMenuOffen] = useState(false);

  const abmelden = () => {
    localStorage.removeItem('portal_token');
    navigate('/login');
  };

  const menue = (
    <>
      <Toolbar sx={{ height: TOPBAR, minHeight: `${TOPBAR}px !important` }} />
      <List sx={{ px: 1.5, pt: 2 }}>
        {PUNKTE.map((p) => {
          const aktiv = location.pathname === p.pfad || location.pathname.startsWith(`${p.pfad}/`);
          return (
            <ListItemButton
              key={p.pfad}
              selected={aktiv}
              onClick={() => { navigate(p.pfad); setMenuOffen(false); }}
              sx={{
                borderRadius: 2, mb: 0.5,
                bgcolor: aktiv ? 'rgba(245,196,0,0.15)' : 'transparent',
                color: aktiv ? 'var(--pw-t1)' : 'var(--pw-t2)',
                '&:hover': { bgcolor: 'rgba(245,196,0,0.08)', color: 'var(--pw-t1)' },
                '&.Mui-selected': { bgcolor: 'rgba(245,196,0,0.15)' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(245,196,0,0.18)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: aktiv ? '#C49B00' : 'inherit' }}>{p.icon}</ListItemIcon>
              <ListItemText primary={p.label} primaryTypographyProps={{ fontWeight: aktiv ? 700 : 500, fontSize: '0.9rem' }} />
            </ListItemButton>
          );
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: 'var(--pw-paper)',
          borderBottom: '1px solid rgba(245,196,0,0.25)',
          color: 'var(--pw-t1)',
        }}
      >
        <Toolbar sx={{ height: TOPBAR, minHeight: `${TOPBAR}px !important`, gap: 1.5 }}>
          {!breit && (
            <IconButton edge="start" aria-label="Menü" onClick={() => setMenuOffen((o) => !o)} sx={{ color: 'var(--pw-t1)' }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box
            onClick={() => navigate('/uebersicht')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, cursor: 'pointer', minWidth: 0 }}
          >
            <Logo hoehe={38} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.15, fontSize: '1.05rem' }}>ProfiWare</Typography>
              <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pw-t4)', lineHeight: 1.2 }}>
                Lizenzportal
              </Typography>
            </Box>
          </Box>
          <Tooltip title={darkMode ? 'Helles Design' : 'Dunkles Design'}>
            <IconButton onClick={toggleDarkMode} aria-label="Design umschalten" sx={{ color: 'var(--pw-t4)', '&:hover': { color: '#C49B00' } }}>
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Abmelden">
            <IconButton onClick={abmelden} aria-label="Abmelden" sx={{ color: 'var(--pw-t4)', '&:hover': { color: '#EF4444' } }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={breit ? 'permanent' : 'temporary'}
        open={breit || menuOffen}
        onClose={() => setMenuOffen(false)}
        sx={{
          width: breit ? DRAWER_BREITE : 0, flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_BREITE, boxSizing: 'border-box', bgcolor: 'var(--pw-sidebar)' },
        }}
      >
        {menue}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 }, bgcolor: 'var(--pw-bg)', minHeight: '100vh' }}>
        <Toolbar sx={{ height: TOPBAR, minHeight: `${TOPBAR}px !important` }} />
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
}

function AppInnen() {
  const { darkMode } = useThemeMode();
  const theme = React.useMemo(() => createAppTheme(darkMode), [darkMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <HashRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/aktivierung" element={<Aktivierung />} />
          <Route path="/verlaengerung" element={<Verlaengerung />} />
          <Route path="/uebersicht" element={<Geschuetzt><Uebersicht /></Geschuetzt>} />
          <Route path="/kunden" element={<Geschuetzt><Kunden /></Geschuetzt>} />
          <Route path="/kunden/:id" element={<Geschuetzt><Kundenakte /></Geschuetzt>} />
          <Route path="/installationen" element={<Geschuetzt><Installationen /></Geschuetzt>} />
          <Route path="/releases" element={<Geschuetzt><Releases /></Geschuetzt>} />
          <Route path="/protokoll" element={<Geschuetzt><Protokoll /></Geschuetzt>} />
          <Route path="/einstellungen" element={<Geschuetzt><Einstellungen /></Geschuetzt>} />
          {/* alte Lesezeichen */}
          <Route path="/dashboard" element={<Navigate to="/uebersicht" replace />} />
          <Route path="/lizenzen" element={<Navigate to="/kunden" replace />} />
          <Route path="*" element={<StartWeiche />} />
        </Routes>
      </HashRouter>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ThemeAnbieter>
      <AppInnen />
    </ThemeAnbieter>
  );
}
